/** Iter 115 — Quotes list panel (vendor dashboard, Bookings tab).
 * Shows every shared quote with its live status; copy/open link, cancel
 * pending ones, jump to the booking once approved. */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Money from "@/components/Money";
import { Button } from "@/components/ui/button";
import { FileText, Copy, ExternalLink, XCircle, ChevronDown, ChevronUp, RefreshCw, Bell } from "lucide-react";

const STATE_STYLE = {
  sent: "bg-blue-50 text-blue-700 border-blue-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

export default function VendorQuotesPanel() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/vendor/quotes").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (q) => {
    try {
      await api.post(`/vendor/quotes/${q.id}/cancel`);
      toast.success("Quote cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!items || items.length === 0) return null;
  const pending = items.filter((q) => q.state === "sent").length;

  return (
    <div className="mb-4 rounded-2xl border border-border bg-white" data-testid="vendor-quotes-panel">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3" data-testid="vendor-quotes-toggle">
        <span className="flex items-center gap-2 text-sm font-bold">
          <FileText className="h-4 w-4 text-blue-600" /> Quotes
          <span className="text-xs font-semibold text-neutral-500">{items.length} total{pending ? ` · ${pending} awaiting approval` : ""}</span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-neutral-400" /> : <ChevronDown className="h-4 w-4 text-neutral-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          <div className="flex justify-end">
            <button type="button" onClick={load} className="text-xs text-neutral-500 inline-flex items-center gap-1 hover:text-neutral-800" data-testid="vendor-quotes-refresh">
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
          {items.map((q) => (
            <div key={q.id} className="rounded-xl border border-border p-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm" data-testid={`quote-row-${q.id}`}>
              <span className={`text-[10px] font-bold uppercase tracking-wide border rounded-full px-2 py-0.5 ${STATE_STYLE[q.state] || STATE_STYLE.cancelled}`} data-testid={`quote-state-${q.id}`}>
                {q.state}
              </span>
              <span className="font-semibold">{q.car_name}</span>
              <span className="text-neutral-500 text-xs">{(q.start_date || "").slice(0, 10)} → {(q.end_date || "").slice(0, 10)}</span>
              <span className="font-bold tabular-nums"><Money amount={q.grand_total} /></span>
              {q.customer_name || q.customer_email ? (
                <span className="text-xs text-neutral-500 truncate max-w-[180px]">{q.customer_name || q.customer_email}</span>
              ) : null}
              {q.reminder_sent_at && q.state === "sent" && (
                <span className="text-[10px] text-amber-600 inline-flex items-center gap-0.5" title="Expiry reminder sent"><Bell className="h-3 w-3" /> reminded</span>
              )}
              <span className="ml-auto flex items-center gap-1.5">
                {q.state === "sent" && (
                  <>
                    <span className="text-[11px] text-neutral-400">expires {(q.expires_at || "").slice(0, 10)}</span>
                    <Button size="sm" variant="outline" className="h-7 rounded-full text-xs px-2" data-testid={`quote-copy-${q.id}`}
                      onClick={() => { navigator.clipboard?.writeText(q.url); toast.success("Link copied"); }}>
                      <Copy className="h-3 w-3 mr-1" /> Copy link
                    </Button>
                    <a href={q.url} target="_blank" rel="noreferrer" className="text-blue-600" title="Open quote page" data-testid={`quote-open-${q.id}`}>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    <button type="button" onClick={() => cancel(q)} className="text-rose-500" title="Cancel quote" data-testid={`quote-cancel-${q.id}`}>
                      <XCircle className="h-4 w-4" />
                    </button>
                  </>
                )}
                {q.state === "approved" && q.booking_id && (
                  <span className="text-xs font-bold text-emerald-700" data-testid={`quote-booking-${q.id}`}>
                    Booking #{q.booking_id.slice(0, 8).toUpperCase()}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
